import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { findNodesByType } from "@iventis/tree-browser";
import { compareAsc } from "date-fns";
import type { SidebarOrderLayer } from "./sidebar-order-helpers";

/** The layers of a stream sit above its stages, so a node's type decides where it goes before any order does */
const getNodeTypePrecedence = (node: Node) => (node.type === NodeType.Layer ? 0 : 1);

/** The first layer inside a stage supplies its sidebar order and its library node supplies its creation date */
const findFirstDescendantLayer = (node: Node, layers: SidebarOrderLayer[]): SidebarOrderLayer => {
    const layerNodes = findNodesByType([node], NodeType.Layer);
    const layerNode = layerNodes.find((node) => layers.some((layer) => layer.id === node.id));
    const layer = layers.find((layer) => layer.id === layerNode?.id);
    return layer == null ? null : { ...layer, createdAt: layerNode.createdAt };
};

const getLayerOrder = (layer: SidebarOrderLayer) => layer?.sidebarOrder ?? Infinity;

/** Compares two layers the way the rest of the sidebar does: sidebarOrder, then creation for those without one */
const compareLayers = (a: SidebarOrderLayer, b: SidebarOrderLayer) => {
    const aOrder = getLayerOrder(a);
    const bOrder = getLayerOrder(b);
    if (aOrder !== bOrder) {
        return aOrder - bOrder;
    }
    // Creation dates are stored on library nodes; the layer response does not populate them.
    return a?.createdAt != null && b?.createdAt != null ? compareAsc(a.createdAt, b.createdAt) : 0;
};

/**
 * Orders what a stream shows: its own layers (transfers, breaks) first, then its stages in the order of the layers they
 * hold, which is what the backend renumbers when the timetable moves a stage
 */
export const sortStreamChildNodes = (children: Node[], layers: SidebarOrderLayer[]) =>
    [...children].sort((a, b) => {
        const typeDifference = getNodeTypePrecedence(a) - getNodeTypePrecedence(b);
        if (typeDifference !== 0) {
            return typeDifference;
        }
        if (a.type === NodeType.Layer) {
            return compareLayers(
                layers.find((l) => l.id === a.id),
                layers.find((l) => l.id === b.id)
            );
        }
        return compareLayers(findFirstDescendantLayer(a, layers), findFirstDescendantLayer(b, layers));
    });

/** The stages of a stream in the order the sidebar shows them, which is also the order their badges number them in */
export const getOrderedStageNodes = (streamNode: Node, layers: SidebarOrderLayer[]) =>
    sortStreamChildNodes(streamNode?.childNodes ?? [], layers).filter((node) => node.type === NodeType.Stage);
